import { keepPreviousData, queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import { api } from '@/api';
import { DateRangeParams } from '@/api/apis/resources/schedule';
import { scheduleQueryKeys } from '@/queries/resource/schedule/schedule.keys';

export const ccScheduleQueries = {
  shifts: (dateRange: DateRangeParams, processId?: string | null) => {
    const queryFn = () =>
      processId ? api.schedule.cc.getCCPlanningShifts(processId, dateRange) : api.schedule.cc.getCCShifts(dateRange);

    return queryOptions({
      queryKey: scheduleQueryKeys.cc.getShiftsKey(dateRange, processId),
      queryFn: () => queryFn().then((data) => data.resourcesShifts),
    });
  },
  schedule: (startDayOfMonth: Date) =>
    queryOptions({
      queryKey: scheduleQueryKeys.cc.getScheduleKey(startDayOfMonth),
      queryFn: () => api.schedule.cc.getCCSchedule(startDayOfMonth),
    }),
  planningProcess: (processId?: string | null) =>
    queryOptions({
      queryKey: scheduleQueryKeys.cc.getPlanningProcessKey(processId),
      queryFn: processId ? () => api.schedule.cc.getCCPlanningProcessById(processId) : skipToken,
    }),
  validation: (scheduleId?: string) =>
    queryOptions({
      queryKey: scheduleQueryKeys.cc.getValidationKey(scheduleId),
      queryFn: scheduleId ? () => api.schedule.cc.getCCValidation(scheduleId) : skipToken,
    }),
  rules: () =>
    queryOptions({
      queryKey: scheduleQueryKeys.cc.getRulesKey(),
      queryFn: () => api.schedule.cc.getCCScheduleRules(),
    }),
  inputs: () =>
    queryOptions({
      queryKey: scheduleQueryKeys.cc.getInputsKey(),
      queryFn: () => api.schedule.cc.getCCScheduleInputs(),
    }),
  hrSystemConflicts: (scheduleId?: string) =>
    queryOptions({
      queryKey: scheduleQueryKeys.cc.getHrSystemConflictsKey(scheduleId),
      queryFn: scheduleId ? () => api.schedule.cc.getCCScheduleHrSystemConflicts(scheduleId) : skipToken,
    }),
};

export const useCCScheduleShiftsQuery = (dateRange: DateRangeParams, processId?: string | null, isPlanned = false) => {
  return useQuery({
    ...ccScheduleQueries.shifts(dateRange, isPlanned ? processId : undefined),
    placeholderData: keepPreviousData,
  });
};

export const useCCMonthScheduleQuery = (startDayOfMonth: Date, refetchTime?: number) => {
  return useQuery({
    ...ccScheduleQueries.schedule(startDayOfMonth),
    refetchInterval: refetchTime,
  });
};

export const useCCValidationQuery = (scheduleId?: string, enabled = false) => {
  return useQuery({
    ...ccScheduleQueries.validation(scheduleId),
    enabled: Boolean(scheduleId && enabled),
  });
};

export const useCCPlanningProcessQuery = (processId?: string | null, enabled = false, refetchTime?: number) => {
  return useQuery({
    ...ccScheduleQueries.planningProcess(processId),
    enabled: Boolean(processId && enabled),
    refetchInterval: refetchTime,
  });
};

export const useCCScheduleSettingsRulesQuery = () => {
  return useQuery(ccScheduleQueries.rules());
};

export const useCCScheduleSettingsInputsQuery = () => {
  return useQuery(ccScheduleQueries.inputs());
};

export const useCCHrSystemConflictsQuery = (scheduleId?: string, enabled = false) => {
  return useQuery({
    ...ccScheduleQueries.hrSystemConflicts(scheduleId),
    enabled: Boolean(scheduleId && enabled),
  });
};
